import React, { useEffect, useState, useRef } from 'react';

interface LessonCompleteCheckmarkProps {
  show: boolean;
  onDone?: () => void;
}

/** SVG checkmark that draws itself via stroke-dashoffset over ~400ms */
export default function LessonCompleteCheckmark({ show, onDone }: LessonCompleteCheckmarkProps) {
  const [animate, setAnimate] = useState(false);
  const pathRef = useRef<SVGPathElement | null>(null);
  const [pathLen, setPathLen] = useState<number>(0);

  useEffect(() => {
    if (!show) {
      setAnimate(false);
      return;
    }
    const t = requestAnimationFrame(() => setAnimate(true));
    // compute path length for accurate stroke-dasharray
    if (pathRef.current) {
      try {
        const len = pathRef.current.getTotalLength();
        setPathLen(len);
      } catch {
        setPathLen(40);
      }
    }

    const done = setTimeout(() => onDone?.(), 450);
    return () => {
      cancelAnimationFrame(t);
      clearTimeout(done);
    };
  }, [show, onDone]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center pointer-events-none">
      <div className="w-24 h-24 rounded-full bg-[#141414]/95 border border-green-500/40 flex items-center justify-center shadow-2xl">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden>
          <circle cx="24" cy="24" r="22" stroke="#22c55e" strokeWidth="2" opacity="0.35" />
          <path
            ref={pathRef}
            d="M14 24 L21 31 L34 17"
            stroke="#22c55e"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
            strokeDasharray={pathLen || 40}
            strokeDashoffset={animate ? 0 : pathLen || 40}
            style={{ transition: 'stroke-dashoffset 400ms ease-out' }}
          />
        </svg>
      </div>
    </div>
  );
}
